import type Repository from "@shared/services/api/abstracts/Repository";

export type QueryKeyPart =
  | string
  | number
  | boolean
  | object
  | Array<QueryKeyPart>;

/**
 * A class for managing and generating unique query keys based on repository details and provided key parts.
 *
 * @example
 * const repo = new SomeRepository();
 * const queryKeyManager = new QueryKeyManager(repo);
 * const listQueryKey = queryKeyManager.generateKey(['list', { page: 1 }]);
 * // Returns ['SomeRepository', 'list', '{"page":1}']
 */
export class QueryKeyManager {
  private readonly repositoryName: string;

  private readonly repositoryInjects: Record<string, unknown>;

  /**
   * Initializes a new instance of the QueryKeyManager with the name of the repository.
   * The repository name is used as the first part of all generated query keys to
   * ensure they are unique to the repository.
   *
   * @param {RepositoryContract} repository The repository this manager is associated with.
   */
  constructor(repository: Repository<unknown>) {
    this.repositoryName = repository.getRepositoryName() || "UnknownRepository";
    this.repositoryInjects = repository.getInjects();
  }

  /**
   * Serializes an object with its keys sorted alphabetically.
   */
  private static serializeWithSortedKeys(obj: Record<string, unknown>): string {
    return JSON.stringify(
      Object.keys(obj)
        .sort() // Sort keys alphabetically
        .reduce<Record<string, unknown>>((sorted, key) => {
          return { ...sorted, [key]: obj[key] }; // Create a new object
        }, {})
    );
  }

  /**
   * Adds the repository injects and any additional injects to the parts array.
   */
  private insertInjects(
    parts: QueryKeyPart[],
    injects?: Record<string, unknown>
  ): QueryKeyPart[] {
    const hasRepositoryInjects =
      this.repositoryInjects && Object.keys(this.repositoryInjects).length > 0;
    const hasInjects = injects && Object.keys(injects).length > 0;
    if (hasRepositoryInjects || hasInjects) {
      // Merge and sort keys before serializing
      const combinedInjects = {
        ...this.repositoryInjects,
        ...injects,
      };

      const injectsKey =
        QueryKeyManager.serializeWithSortedKeys(combinedInjects);

      // first index of part is usually reserved for the url name
      // so insert after url part, if it exists
      const insertIndex = parts.length > 0 ? 1 : 0;
      parts.splice(insertIndex, 0, injectsKey);
    }
    return parts;
  }

  generateKey(
    parts: QueryKeyPart[],
    injects?: Record<string, unknown>
  ): string[] {
    const keyParts = this.insertInjects(parts, injects);
    // Helper function to recursively flatten and serialize parts of the key
    const flattenParts = (part: QueryKeyPart): string => {
      if (Array.isArray(part)) {
        // Recursively flatten array parts and join with '/'
        return part.map(flattenParts).join("/");
      }
      if (typeof part === "object") {
        // Serialize objects to JSON strings
        return JSON.stringify(part);
      }
      // Convert all other types to strings
      return String(part);
    };

    // Map each part through flattenParts and prepend the repository's name
    const key = keyParts.map(flattenParts);
    key.unshift(this.repositoryName);
    return key;
  }

  /**
   * Returns the first part of the query key, which is the repository name.
   */
  getBaseKey() {
    return this.repositoryName;
  }
}
